/**
 * Browser-compatibility helpers and the support gate.
 *
 * The app's floor is set by WebRTC + LiveKit, not by taste: Chrome/Edge 84,
 * Firefox 78, Safari 14.1 (13.1 usually works), Android WebView 84. Everything
 * newer than the floor that we use is either transpiled by the build target
 * (syntax), polyfilled in polyfills.ts (small standard APIs), or avoided here.
 */

/**
 * `AbortSignal.timeout(ms)` needs Chrome/Edge 103, Firefox 100, Safari 16 —
 * far above our floor, and it was called UNGUARDED on every join and moderation
 * request. On a school PC with Edge 100 the join button simply threw. This is
 * the classic trap: the API is three years old and feels safe, but the machines
 * in an Egyptian school lab are older than that.
 */
export async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  ms: number,
): Promise<Response> {
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), ms)
  try {
    return await fetch(url, { ...init, signal: controller.signal })
  } finally {
    // Cleared on settle either way, so a completed request does not keep a
    // controller alive for the rest of the timeout window.
    window.clearTimeout(timer)
  }
}

/**
 * Can this browser run the class at all?
 *
 * Checked before React mounts, so an unsupported browser gets a clear bilingual
 * message instead of a white page or a cryptic mid-join failure. The list is the
 * genuine floor, not a wishlist: WebRTC transport, media capture, the socket the
 * signalling rides on, and ResizeObserver (used unguarded by us and by LiveKit).
 * isSecureContext is checked because getUserMedia simply does not exist on plain
 * http — a misconfigured kiosk would otherwise look like a broken app.
 */
export function browserSupported(): boolean {
  try {
    return (
      // Prefixed implementations count: livekit's adapter normalises them.
      (typeof window.RTCPeerConnection === 'function' ||
        typeof (window as any).webkitRTCPeerConnection === 'function' ||
        typeof (window as any).mozRTCPeerConnection === 'function') &&
      typeof navigator.mediaDevices?.getUserMedia === 'function' &&
      typeof window.WebSocket === 'function' &&
      typeof window.ResizeObserver === 'function' &&
      // The randomUUID polyfill builds on this; a crypto-less webview must be
      // told to update rather than half-working.
      typeof crypto?.getRandomValues === 'function' &&
      window.isSecureContext !== false
    )
  } catch {
    return false
  }
}

/**
 * Rendered without React (plain DOM), because if we are here the environment is
 * exactly the one we trust least. Arabic first — it is the product's default
 * language — then English.
 */
export function renderUnsupported(root: HTMLElement): void {
  root.innerHTML = ''
  const wrap = document.createElement('div')
  wrap.className = 'unsupported'
  wrap.setAttribute('dir', 'rtl')
  wrap.innerHTML = [
    '<div class="unsupported__card">',
    '<p class="unsupported__icon">🌐</p>',
    '<h1>هذا المتصفح قديم ولا يدعم الحصص المباشرة</h1>',
    '<p>حدّث المتصفح أو جرّب أحدث إصدار من Chrome أو Edge أو Firefox ثم افتح الرابط مرة أخرى.</p>',
    '<hr>',
    '<h2 dir="ltr">This browser is too old for live classes</h2>',
    '<p dir="ltr">Please update it, or try the latest Chrome, Edge or Firefox, then open your link again.</p>',
    '</div>',
  ].join('')
  root.appendChild(wrap)
}
